(() => {
  if (customElements.get("gift-note-handler")) return;

  const ROOT = window.Shopify?.routes?.root || "/";
  const CART_ADD = `${ROOT}cart/add.js`;
  const CART_CHANGE = `${ROOT}cart/change.js`;
  const CART_UPDATE = `${ROOT}cart/update.js`;
  const CART_JSON = `${ROOT}cart.js`;

  const THEME_ID = window.Shopify?.theme?.id || "default";
  const PENDING_KEY_PREFIX = `gwp_${THEME_ID}_pending_gift_for_variant_`;

  try {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith("gwp_pending_gift_for_variant_")) {
        sessionStorage.removeItem(key);
      }
    }
  } catch {
    /* ignore */
  }
  
  if (!window.__gwpFetchPatched) {
    window.__gwpFetchPatched = true;
    patchFetchForPdpGiftMerge();
  }

  function isCartAddUrl(url) {
    const u = typeof url === "string" ? url : url?.url || "";
    return u.includes("/cart/add");
  }

  function safeJSONParse(str) {
    try {
      return JSON.parse(str);
    } catch {
      return null;
    }
  }

  async function fetchJSON(url, options = {}) {
    const res = await fetch(url, { credentials: "same-origin", ...options });
    const text = await res.text();
    const data = safeJSONParse(text);
    if (!res.ok) throw new Error(`Request failed (${res.status}): ${text || url}`);
    return data ?? text;
  }

  async function getCart() {
    return fetchJSON(CART_JSON, { method: "GET" });
  }

  function cartHasVariant(cart, variantId) {
    const id = Number(variantId);
    return !!cart?.items?.some((i) => Number(i.variant_id) === id);
  }

  function getGiftItemFromCart(cart, giftVariantId) {
    const id = Number(giftVariantId);
    return cart?.items?.find((i) => Number(i.variant_id) === id) || null;
  }

  function publishDawnCartUpdate(cartData, source = "gift-note-handler", variantId = undefined) {
    try {
      if (typeof publish === "function" && window.PUB_SUB_EVENTS?.cartUpdate) {
        publish(window.PUB_SUB_EVENTS.cartUpdate, { source, cartData, variantId });
      }
    } catch {
      /* ignore */
    }
  }

  function escapeHTML(str) {
    return String(str)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");
  }

  function normalizeGiftNoteValue(value, limit) {
    if (!limit) return value || "";
    const n = Number(limit);
    if (!n) return value || "";
    return String(value || "").slice(0, n);
  }

  function saveGiftNoteState() {
    const handler = document.querySelector("gift-note-handler");
    if (!handler) return null;
    const details = handler.querySelector("[data-gift-note-details]");
    return {
      isOpen: !!details?.open,
      noteValue: handler.field?.value || "",
    };
  }

  function restoreGiftNoteState(state) {
    if (!state) return;
    // Wait a tick for new handler to mount
    requestAnimationFrame(() => {
      const handler = document.querySelector("gift-note-handler");
      if (!handler) return;
      const details = handler.querySelector("[data-gift-note-details]");
      const body = handler.querySelector(".gift-note__body");

      if (state.isOpen && details && !details.open) {
        details.setAttribute("open", "");
        if (body) {
          body.style.gridTemplateRows = "1fr";
          body.style.opacity = "1";
        }
      }
    });
  }

  function updatePreview(handler, value) {
    const preview = handler.querySelector("[data-gift-note-preview]");
    if (!preview) return;
    if (!value) {
      preview.innerHTML = "";
      return;
    }
    preview.innerHTML = `
      <div class="group-open:tw-hidden tw-block tw-pt-3">
        <div class="tw-flex tw-flex-row tw-justify-between">
          <p class="tw-font-semibold tw-text-body">Message preview</p>
          <p class="tw-underline tw-text-body">Edit</p>
        </div>
        <p class="preview-text tw-mt-2 tw-text-body">${escapeHTML(value)}</p>
      </div>`;
  }

  function syncAllHandlers(attributeKey, noteValue, checked, { syncCheckbox = true } = {}) {
    const handlers = [...document.querySelectorAll("gift-note-handler")];
    handlers.forEach((h) => {
      if (!h.field || !h.giftCheckbox) return;
      if (h.field.getAttribute("name") === `attributes[${attributeKey}]`) {
        h.field.value = noteValue;
        h.updateTextAreaLimitText({ target: h.field });
      }
      if (syncCheckbox) {
        h.isGiftCheckboxChecked = !!checked;
        h.giftCheckbox.classList.toggle("checked", !!checked);
      }
    });
  }

  function setPending(giftVariantId, on) {
    try {
      const key = `${PENDING_KEY_PREFIX}${Number(giftVariantId)}`;
      if (on) {
        // Store with timestamp — expires after 5 minutes
        sessionStorage.setItem(key, String(Date.now()));
      } else {
        sessionStorage.removeItem(key);
      }
    } catch {
      /* ignore */
    }
  }

  function isPending(giftVariantId) {
    try {
      const key = `${PENDING_KEY_PREFIX}${Number(giftVariantId)}`;
      const val = sessionStorage.getItem(key);
      if (!val) return false;

      const timestamp = Number(val);
      // Expire after 5 minutes
      if (Date.now() - timestamp > 5 * 60 * 1000) {
        sessionStorage.removeItem(key);
        return false;
      }
      return true;
    } catch {
      return false;
    }
  }

  function inferContext(el) {
    const explicit = el?.dataset?.context;
    if (explicit === "cart" || explicit === "pdp") return explicit;
    if (el.closest("cart-drawer") || el.closest("cart-drawer-items") || el.closest("cart-items")) return "cart";
    if (document.body.classList.contains("template-cart")) return "cart";
    if (document.body.classList.contains("template-product")) return "pdp";
    if (el.closest('form[action*="/cart/add"]') || el.closest("product-form")) return "pdp";
    return "cart";
  }

  async function refreshCartDrawerSectionAndBubbleSilently() {
    try {
      const savedState = saveGiftNoteState();
      const url = `${ROOT}cart?sections=${encodeURIComponent("cart-drawer,cart-icon-bubble")}`;
      const sections = await fetch(url, { credentials: "same-origin" }).then((r) => r.json());

      if (sections["cart-icon-bubble"]) {
        const doc = new DOMParser().parseFromString(sections["cart-icon-bubble"], "text/html");
        const newEl = doc.querySelector("#cart-icon-bubble");
        const curEl = document.querySelector("#cart-icon-bubble");
        if (newEl && curEl) curEl.innerHTML = newEl.innerHTML;
      }

      if (sections["cart-drawer"]) {
        const doc = new DOMParser().parseFromString(sections["cart-drawer"], "text/html");
        const newEl = doc.querySelector("#CartDrawer");
        const curEl = document.querySelector("#CartDrawer");
        if (newEl && curEl) curEl.innerHTML = newEl.innerHTML;
      }
      restoreGiftNoteState(savedState);
      try {
        const cart = await getCart();
        const drawerEl = document.querySelector("cart-drawer");
        if (drawerEl && typeof cart?.item_count === "number") {
          drawerEl.classList.toggle("is-empty", cart.item_count === 0);
        }
      } catch {
        /* ignore */
      }
    } catch {
      /* ignore */
    }
  }

  async function refreshDawnDrawer(cartDataForPublish, { openDrawer = true } = {}) {
    const drawer = document.querySelector("cart-drawer");

    if (!openDrawer || !drawer || typeof drawer.renderContents !== "function") {
      if (cartDataForPublish) publishDawnCartUpdate(cartDataForPublish);
      await refreshCartDrawerSectionAndBubbleSilently();
      return;
    }

    try {
      const savedState = saveGiftNoteState();
      const url = `${ROOT}cart?sections=${encodeURIComponent("cart-drawer,cart-icon-bubble")}`;
      const sections = await fetch(url, { credentials: "same-origin" }).then((r) => r.json());
      const parsedState = { id: cartDataForPublish?.token || Date.now(), sections };
      drawer.renderContents(parsedState);
      if (cartDataForPublish) publishDawnCartUpdate(cartDataForPublish);
      restoreGiftNoteState(savedState);
    } catch {
      if (cartDataForPublish) publishDawnCartUpdate(cartDataForPublish);
    }
  }

  async function addGiftNow(giftVariantId, noteValue = "") {
    await fetchJSON(CART_ADD, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        items: [{ id: Number(giftVariantId), quantity: 1, properties: { "Gift Note": noteValue || "" } }],
      }),
    });
    const cart = await getCart();
    await refreshDawnDrawer(cart);
    return cart;
  }

  async function removeGiftNow(giftVariantId, attributeKey) {
    const cart = await getCart();
    const giftItem = getGiftItemFromCart(cart, giftVariantId);
    if (!giftItem) return cart;

    await Promise.all([
      fetchJSON(CART_CHANGE, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: giftItem.key, quantity: 0 }),
      }),
      attributeKey
        ? fetchJSON(CART_UPDATE, {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ attributes: { [attributeKey]: "" } }),
          })
        : Promise.resolve(),
    ]);

    const updatedCart = await getCart();
    await refreshDawnDrawer(updatedCart, { openDrawer: false });
    return updatedCart;
  }

  function patchFetchForPdpGiftMerge() {
    const originalFetch = window.fetch.bind(window);

    window.fetch = async (input, init = {}) => {
      const url = typeof input === "string" ? input : input?.url || "";
      if (!isCartAddUrl(url)) return originalFetch(input, init);

      const method = (init?.method || (typeof input !== "string" ? input.method : "POST") || "POST").toUpperCase();
      if (method !== "POST") return originalFetch(input, init);

      const body = init?.body;
      const isDawnProductFormAdd =
        body instanceof FormData && body.get("id") && (body.get("sections") || body.get("sections_url"));
      if (!isDawnProductFormAdd) return originalFetch(input, init);

      const pendingHandler = [...document.querySelectorAll("gift-note-handler")].find((h) => {
        const ctx = inferContext(h);
        return ctx === "pdp" && h?.giftVariant && isPending(h.giftVariant);
      });
      if (!pendingHandler) return originalFetch(input, init);

      const giftVariantId = Number(pendingHandler.giftVariant);

      try {
        const mainId = Number(body.get("id"));
        const qty = body.get("quantity");
        const mainQty = qty ? Number(qty) : 1;
        if (!mainId || mainId === giftVariantId) return originalFetch(input, init);

        const props = {};
        for (const [k, v] of body.entries()) {
          if (k.startsWith("properties[")) {
            props[k.slice("properties[".length, -1)] = v;
          }
        }

        const noteVal = normalizeGiftNoteValue(pendingHandler.field?.value || "", pendingHandler.textAreaLimit);
        setPending(giftVariantId, false);

        const mergedPayload = {
          items: [
            { id: mainId, quantity: mainQty, properties: props },
            { id: giftVariantId, quantity: 1, properties: { "Gift Note": noteVal || "" } },
          ],
        };

        const sectionsValue = body.get("sections");
        const sectionsUrlValue = body.get("sections_url");
        if (sectionsValue) mergedPayload.sections = sectionsValue.split(",");
        if (sectionsUrlValue) mergedPayload.sections_url = sectionsUrlValue;

        const mergedInit = {
          ...init,
          method: "POST",
          headers: { ...(init.headers || {}), "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify(mergedPayload),
        };

        const resp = await originalFetch(input, mergedInit);

        try {
          syncAllHandlers(pendingHandler.giftNoteAttribute, noteVal, true);
          fetchJSON(CART_UPDATE, {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ attributes: { [pendingHandler.giftNoteAttribute]: noteVal } }),
          }).catch(() => {});
        } catch {
          /* ignore */
        }

        return resp;
      } catch {
        return originalFetch(input, init);
      }
    };
  }

  class GiftNoteHandler extends HTMLElement {
    constructor() {
      super();

      this.textAreaLimit = this.dataset.giftNoteLimit;
      this.giftVariant = Number(this.dataset.giftVariant);
      this.product = Number(this.dataset.product);
      this.giftNoteAttribute = this.dataset.giftNoteAttribute || "Gift note";
      this.commitOn = this.dataset.commitOn || "live";

      this.field = this.querySelector(`[name="attributes[${this.giftNoteAttribute}]"]`);
      this.limitCounter = this.querySelector("[data-gift-limit-counter]");
      this.details = this.querySelector("[data-gift-note-details]");
      this.giftCheckbox = this.querySelector("[data-enable-gift]");

      this.body = this.querySelector(".gift-note__body");

      this.spinner = this.querySelector("[data-gift-spinner]");
      this.headerText = this.querySelector(".gift-note__header-text");

      const summary = this.details?.querySelector("summary");
      if (summary) {
        summary.addEventListener("click", (e) => {
          if (e.target.closest("[data-enable-gift]")) return;

          e.preventDefault();

          if (this.details.open) {
            this.animateClose();
          } else {
            this.animateOpen();
          }
        });
      }

      this.isGiftCheckboxChecked = false;
      this._lock = false;

      if (!this.field || !this.giftCheckbox) return;

      this.initFromCart();

      this.field.addEventListener("input", () => {
        const nextVal = normalizeGiftNoteValue(this.field.value, this.textAreaLimit);
        this.field.value = nextVal;
        this.updateTextAreaLimitText({ target: this.field });
        syncAllHandlers(this.giftNoteAttribute, nextVal, this.isGiftCheckboxChecked);

        if (this.commitOn === "live") {
          this.debouncedUpdateAttr(nextVal);
        } else {
          if (this.isGiftCheckboxChecked) this.debouncedUpdateAttr(nextVal);
        }
      });

      this.giftCheckbox.addEventListener("click", async (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        if (this._lock) return;
        this._lock = true;
        this.showLoading();

        try {
          const ctx = inferContext(this);
          const cart = await getCart();
          const giftInCart = cartHasVariant(cart, this.giftVariant);
          const goingChecked = !this.isGiftCheckboxChecked;

          if (goingChecked) {
            this.isGiftCheckboxChecked = true;
            this.giftCheckbox.classList.add("checked");
            this.animateOpen();

            const noteVal = normalizeGiftNoteValue(this.field.value, this.textAreaLimit);

            if (ctx === "cart") {
              await addGiftNow(this.giftVariant, noteVal);
              await this.updateGiftNoteAttribute(noteVal);
              syncAllHandlers(this.giftNoteAttribute, noteVal, true);
            } else {
              if (giftInCart) {
                await this.updateGiftNoteAttribute(noteVal);
                syncAllHandlers(this.giftNoteAttribute, noteVal, true);
              } else {
                const hasOtherItems = (cart.items || []).some(
                  (i) => Number(i.variant_id) !== this.giftVariant && Number(i.product_id) !== this.product,
                );

                if (hasOtherItems) {
                  await addGiftNow(this.giftVariant, noteVal);
                  await this.updateGiftNoteAttribute(noteVal);
                  syncAllHandlers(this.giftNoteAttribute, noteVal, true);
                } else {
                  setPending(this.giftVariant, true);
                  syncAllHandlers(this.giftNoteAttribute, noteVal, false, { syncCheckbox: false });
                  this.isGiftCheckboxChecked = true;
                  this.giftCheckbox.classList.add("checked");
                }
              }
            }
          } else {
            this.isGiftCheckboxChecked = false;
            this.giftCheckbox.classList.remove("checked");
            this.animateClose();
            setPending(this.giftVariant, false);

            if (giftInCart) {
              await removeGiftNow(this.giftVariant, this.giftNoteAttribute);
            } else {
              await this.updateGiftNoteAttribute("");
            }

            this.field.value = "";
            this.updateTextAreaLimitText({ target: this.field });
            syncAllHandlers(this.giftNoteAttribute, "", false);
          }
        } catch {
          try {
            const cart = await getCart();
            const giftInCart = cartHasVariant(cart, this.giftVariant);
            this.isGiftCheckboxChecked = giftInCart;
            this.giftCheckbox.classList.toggle("checked", giftInCart);
          } catch {
            /* ignore */
          }
        } finally {
          this.hideLoading();
          this._lock = false;
        }
      });
    }

    debouncedUpdateAttr = this.debounce(async (val) => {
      await Promise.all([this.updateGiftNoteAttribute(val), this.updateGiftItemProperties(val)]);
    }, 500);

    showLoading() {
      this.headerText?.classList.add("gift-note--loading");
      if (this.field) this.field.disabled = true;
      this.body?.classList.add("gift-note--disabled");
      const cartDrawerItems = document.querySelector("cart-drawer-items");
      if (cartDrawerItems) cartDrawerItems.classList.add("cart__items--disabled");
      // Disable checkout button
      const checkoutBtn = document.getElementById("CartDrawer-Checkout");
      if (checkoutBtn) {
        checkoutBtn.setAttribute("disabled", "disabled");
        checkoutBtn.classList.add("loading");
      }
    }

    hideLoading() {
      this.headerText?.classList.remove("gift-note--loading");
      if (this.field) this.field.disabled = false;
      this.body?.classList.remove("gift-note--disabled");
      const cartDrawerItems = document.querySelector("cart-drawer-items");
      if (cartDrawerItems) cartDrawerItems.classList.remove("cart__items--disabled");
      // Re-enable checkout button
      const checkoutBtn = document.getElementById("CartDrawer-Checkout");
      if (checkoutBtn) {
        checkoutBtn.removeAttribute("disabled");
        checkoutBtn.classList.remove("loading");
      }
    }

    animateOpen() {
      if (!this.details || !this.body) {
        this.details?.setAttribute("open", "");
        return;
      }
      this.details.setAttribute("open", "");
      requestAnimationFrame(() => {
        this.body.style.gridTemplateRows = "1fr";
        this.body.style.opacity = "1";
      });
    }

    animateClose() {
      if (!this.details || !this.body) {
        this.details?.removeAttribute("open");
        return;
      }

      if (!this.details.open) {
        this.details.removeAttribute("open");
        this.body.style.gridTemplateRows = "";
        this.body.style.opacity = "";
        return;
      }

      this.body.style.gridTemplateRows = "0fr";
      this.body.style.opacity = "0";

      const onEnd = (ev) => {
        if (ev.propertyName !== "grid-template-rows") return;
        this.body.removeEventListener("transitionend", onEnd);
        this.details.removeAttribute("open");
        this.body.style.gridTemplateRows = "";
        this.body.style.opacity = "";
      };
      this.body.addEventListener("transitionend", onEnd);

      setTimeout(() => {
        this.body.removeEventListener("transitionend", onEnd);
        if (this.details.open) {
          this.details.removeAttribute("open");
        }
        this.body.style.gridTemplateRows = "";
        this.body.style.opacity = "";
      }, 400);
    }

    async initFromCart() {
      try {
        let cart = await getCart();
        if (isPending(this.giftVariant)) {
          const giftInCartAlready = cartHasVariant(cart, this.giftVariant);
          const cartEmpty = !cart.items || cart.items.length === 0;
          if (giftInCartAlready || cartEmpty) {
            setPending(this.giftVariant, false);
          }
        }
        const cleaned = await this.cleanupStaleGiftItems(cart);
        if (cleaned) cart = await getCart();
        const giftInCart = cartHasVariant(cart, this.giftVariant);
        this.isGiftCheckboxChecked = giftInCart;
        this.giftCheckbox.classList.toggle("checked", giftInCart);

        const attrVal = cart?.attributes?.[this.giftNoteAttribute] || "";
        if (attrVal && !this.field.value) {
          this.field.value = normalizeGiftNoteValue(attrVal, this.textAreaLimit);
        }

        this.updateTextAreaLimitText({ target: this.field });
        syncAllHandlers(this.giftNoteAttribute, this.field.value, giftInCart);
      } catch {
        /* ignore */
      }
    }

    debounce(func, timeout = 500) {
      let timer;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => func.apply(this, args), timeout);
      };
    }

    updateTextAreaLimitText(e) {
      const limit = Number(this.textAreaLimit || 0);
      const currentText = e.target.value || "";
      updatePreview(this, currentText);

      if (limit) {
        const truncated = currentText.slice(0, limit);
        e.target.value = truncated;
        const remaining = limit - truncated.length;
        if (this.limitCounter) this.limitCounter.innerText = remaining;
        if (this.limitCounter?.parentElement) {
          this.limitCounter.parentElement.classList.toggle("error", truncated.length === limit);
        }
        const limitError = this.querySelector("[data-gift-limit-error]");
        if (limitError) limitError.style.display = truncated.length === limit ? "inline" : "none";
      }
    }

    async updateGiftNoteAttribute(value) {
      await fetchJSON(CART_UPDATE, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ attributes: { [this.giftNoteAttribute]: value || "" } }),
      });
    }

    async updateGiftItemProperties(value) {
      try {
        const cart = await getCart();
        const giftItem = getGiftItemFromCart(cart, this.giftVariant);
        if (!giftItem) return;

        await fetchJSON(CART_CHANGE, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            id: giftItem.key,
            quantity: 1,
            properties: { "Gift Note": value || "" },
          }),
        });
      } catch {
        /* ignore */
      }
    }

    async cleanupStaleGiftItems(cart) {
      if (this._cleaningUp) return false;
      if (!cart?.items?.length) return false;
      this._cleaningUp = true;

      const currentGiftVariantId = Number(this.giftVariant);
      const currentGiftProductId = Number(this.product);

      const staleGiftItems = cart.items.filter((item) => {
        const hasGiftNoteProperty = item.properties && "Gift Note" in item.properties;
        if (!hasGiftNoteProperty) return false;

        const isCurrentGift =
          Number(item.variant_id) === currentGiftVariantId || Number(item.product_id) === currentGiftProductId;

        return !isCurrentGift;
      });

      if (staleGiftItems.length === 0) return false;

      try {
        await Promise.all(
          staleGiftItems.map((item) =>
            fetchJSON(CART_CHANGE, {
              method: "POST",
              headers: { "Content-Type": "application/json", Accept: "application/json" },
              body: JSON.stringify({ id: item.key, quantity: 0 }),
            }),
          ),
        );

        await refreshCartDrawerSectionAndBubbleSilently();
        this._cleaningUp = false;
        return true;
      } catch {
        this._cleaningUp = false;
        return false;
      }
    }
  }

  customElements.define("gift-note-handler", GiftNoteHandler);
})();
